<script>
import utilsMixin from '@shared/mixins/utils';
import debounce from 'lodash/debounce';
import Store from '@app/services/API/Store';

export default {
  mixins: [utilsMixin],
  head() {
    return {
      title: `Abonnements - ${this.membership ? this.membership.name : 'Plan'}`,
    };
  },
  created() {
    this.fetchResults();
    this.fetchStats();
  },
  watch: {
    statusFilter() {
      this.currentPage = 1;
      this.fetchResults();
    },
  },
  data() {
    return {
      searchTerm: '',
      statusFilter: 'active',
      currentPage: 1,
      perPage: 20,
      sortField: 'created_at',
      sortOrder: 'desc',
      subscribers: { data: [], links: {}, meta: {} },
      loading: false,
      membershipStats: null,
    };
  },
  computed: {
    membership() {
      return this.$store.getters['memberships/current'];
    },
    store() {
      return this.$store.getters['auth/store'];
    },
    hasExpertPlan() {
      return this.$store.getters['auth/hasExpertPlan'];
    },
    filteredSubscribers() {
      return this.subscribers.data;
    },
    totalRevenue() {
      if (!this.membershipStats) return this.$options.filters.formatPrice(0);
      return this.$options.filters.formatPrice(this.membershipStats.monthly_revenue);
    },
    activeSubscribersCount() {
      return this.membershipStats?.active_subscribers_count || 0;
    },
  },
  methods: {
    async fetchResults() {
      this.loading = true;
      try {
        const membershipUUID = this.$route.params.uuid;
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_field: this.sortField,
          sort_order: this.sortOrder,
          search: this.searchTerm || undefined,
          status: this.statusFilter !== 'all' ? this.statusFilter : undefined,
        };

        const response = await Store.getMembershipSubscribers(membershipUUID, params);

        this.subscribers = {
          data: response.data.data || response.data || [],
          meta: response.data.meta || { total: 0, per_page: 20 },
          links: response.data.links || {},
        };
      } catch (error) {
        console.error('Error fetching subscribers:', error);
        this.$buefy.toast.open({
          message: 'Erreur lors du chargement des abonnements',
          type: 'is-danger',
        });
        this.subscribers = {
          data: [],
          meta: { total: 0, per_page: 20 },
        };
      } finally {
        this.loading = false;
      }
    },
    async fetchStats() {
      try {
        const membershipUUID = this.$route.params.uuid;
        const response = await Store.getMembershipSubscriberStats(membershipUUID);
        this.membershipStats = response.data || response;
      } catch (error) {
        console.error('Error fetching membership stats:', error);
      }
    },
    onSearch: debounce(function(value) {
      this.searchTerm = value.trim();
      this.currentPage = 1;
      this.fetchResults();
    }, 300),
    onSort(sort, order) {
      this.sortField = sort;
      this.sortOrder = order;
      this.fetchResults();
    },
    onPageChange(page) {
      this.currentPage = page;
      window.scroll(0, 0);
      this.fetchResults();
    },
    toggleOrder() {
      this.sortOrder = this.sortOrder !== 'desc' ? 'desc' : 'asc';
      this.fetchResults();
    },
    formatDate(date) {
      return this.$moment.utc(date).local().format('DD/MM/YY [à] HH:mm');
    },
    formatDateFromNow(date) {
      return this.$moment.utc(date).local().fromNow();
    },
    getStatusTag(status) {
      const statusMap = {
        active: { type: 'is-success', text: 'Actif' },
        cancelled: { type: 'is-danger', text: 'Annulé' },
        canceled: { type: 'is-danger', text: 'Annulé' },
        expired: { type: 'is-warning', text: 'Expiré' },
        pending: { type: 'is-info', text: 'En attente' },
        cancel_at_period_end: { type: 'is-warning', text: 'Annulation programmée' },
        paused: { type: 'is-warning', text: 'En pause' },
      };
      return statusMap[status] || { type: 'is-light', text: status };
    },
    formatPrice(value) {
      return this.$options.filters.formatPrice(value);
    },
    getSubscriptionDuration(startDate) {
      if (!startDate) return '';
      const start = this.$moment.utc(startDate);
      const now = this.$moment();
      const duration = this.$moment.duration(now.diff(start));

      if (duration.asMonths() >= 1) {
        const months = Math.floor(duration.asMonths());
        return `${months} mois`;
      } if (duration.asDays() >= 1) {
        const days = Math.floor(duration.asDays());
        return `${days} jour${days > 1 ? 's' : ''}`;
      }
      return 'Nouveau';
    },
    getEffectivePrice(row) {
      if (!row.price || !row.price.amount) return 0;

      // Check for configured discount_price on the membership price first
      if (row.price.discount_price && row.price.discount_price < row.price.amount) {
        return row.price.discount_price;
      }

      const basePrice = row.price.amount;

      // If there's a discount code applied, calculate the discounted price
      if (row.discount_code && row.discount_type && row.discount_value) {
        if (row.discount_type === 'PERCENTAGE') {
          // Apply percentage discount
          const discountAmount = (basePrice * row.discount_value) / 100;
          return Math.max(0, basePrice - discountAmount);
        } if (row.discount_type === 'AMOUNT') {
          // Apply amount discount
          return Math.max(0, basePrice - row.discount_value);
        }
      }

      // Return full amount if no discount
      return basePrice;
    },
    hasDiscount(row) {
      // Check for configured discount_price on the membership price
      if (row.price && row.price.discount_price && row.price.discount_price < row.price.amount) {
        return true;
      }
      // Check for discount code
      return row.discount_code && row.discount_type && row.discount_value && row.discount_value > 0;
    },
    getSubscriptionFrequency(row) {
      if (!row.price || !row.price.interval) return '';

      const { interval } = row.price;
      const count = row.price.interval_count || 1;

      if (interval === 'month') {
        return count === 1 ? 'Mensuel' : `Tous les ${count} mois`;
      } if (interval === 'year') {
        return count === 1 ? 'Annuel' : `Tous les ${count} ans`;
      } if (interval === 'week') {
        return count === 1 ? 'Hebdomadaire' : `Toutes les ${count} semaines`;
      } if (interval === 'day') {
        return count === 1 ? 'Quotidien' : `Tous les ${count} jours`;
      }

      return `${count} ${interval}(s)`;
    },
    async cancelMembership(row) {
      this.$buefy.dialog.confirm({
        title: 'Annuler l\'abonnement',
        message: `
          <p class="mb-3">
            Êtes-vous sûr de vouloir annuler l'abonnement de <strong>${row.customer.firstname} ${row.customer.lastname}</strong> ?
          </p>
          <p>
            L'abonnement restera actif jusqu'à la fin de la période de facturation en cours.
          </p>
        `,
        confirmText: 'Annuler l\'abonnement',
        cancelText: 'Fermer',
        type: 'is-warning',
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          try {
            await Store.cancelCustomerMembership(this.store.uuid, row.uuid);
            this.$buefy.toast.open({
              message: 'Abonnement annulé avec succès',
              type: 'is-success',
            });
            await this.fetchResults();
          } catch (error) {
            console.error('Error canceling membership:', error);
            this.$buefy.toast.open({
              message: 'Erreur lors de l\'annulation de l\'abonnement',
              type: 'is-danger',
            });
          } finally {
            loader.close();
          }
        },
      });
    },
    cancelAllMemberships() {
      const activeCount = this.filteredSubscribers.filter((s) => s.status === 'active' || s.status === 'cancel_at_period_end').length;

      this.$buefy.dialog.confirm({
        title: 'Annuler tous les abonnements',
        message: `
          <p class="mb-3">
            Êtes-vous sûr de vouloir annuler <strong>tous les abonnements</strong> de ce plan ?
          </p>
          <p class="mb-3">
            Cela concernera <strong>${activeCount} abonnement${activeCount > 1 ? 's' : ''}</strong> actif${activeCount > 1 ? 's' : ''}.
          </p>
          <p>
            Les abonnements resteront actifs jusqu'à la fin de leur période de facturation en cours.
          </p>
        `,
        confirmText: 'Annuler tous les abonnements',
        cancelText: 'Fermer',
        type: 'is-warning',
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          try {
            await Store.cancelAllMemberships(this.store.uuid, this.membership.uuid);
            this.$buefy.toast.open({
              message: 'Annulation de tous les abonnements en cours... Cela peut prendre quelques minutes.',
              type: 'is-info',
              duration: 5000,
            });
            await this.fetchResults();
          } catch (error) {
            console.error('Error canceling all memberships:', error);
            this.$buefy.toast.open({
              message: 'Erreur lors de l\'annulation des abonnements',
              type: 'is-danger',
            });
          } finally {
            loader.close();
          }
        },
      });
    },
    async pauseMembership(row) {
      this.$buefy.dialog.confirm({
        title: 'Mettre en pause l\'abonnement',
        message: `
          <p class="mb-3">
            Êtes-vous sûr de vouloir mettre en pause l'abonnement de <strong>${row.customer.firstname} ${row.customer.lastname}</strong> ?
          </p>
          <p>
            L'apprenant ne pourra plus accéder aux formations tant que l'abonnement est en pause.
          </p>
        `,
        confirmText: 'Mettre en pause',
        cancelText: 'Annuler',
        type: 'is-warning',
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          try {
            await Store.pauseCustomerMembership(this.store.uuid, row.uuid);
            this.$buefy.toast.open({
              message: 'Abonnement mis en pause',
              type: 'is-success',
            });
            await this.fetchResults();
          } catch (error) {
            console.error('Error pausing membership:', error);
            this.$buefy.toast.open({
              message: 'Erreur lors de la mise en pause de l\'abonnement',
              type: 'is-danger',
            });
          } finally {
            loader.close();
          }
        },
      });
    },
    async resumeMembership(row) {
      this.$buefy.dialog.confirm({
        title: 'Reprendre l\'abonnement',
        message: `
          <p class="mb-3">
            Êtes-vous sûr de vouloir reprendre l'abonnement de <strong>${row.customer.firstname} ${row.customer.lastname}</strong> ?
          </p>
          <p>
            L'apprenant pourra à nouveau accéder aux formations.
          </p>
        `,
        confirmText: 'Reprendre',
        cancelText: 'Annuler',
        type: 'is-success',
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          try {
            await Store.resumePausedMembership(this.store.uuid, row.uuid);
            this.$buefy.toast.open({
              message: 'Abonnement repris',
              type: 'is-success',
            });
            await this.fetchResults();
          } catch (error) {
            console.error('Error resuming membership:', error);
            this.$buefy.toast.open({
              message: 'Erreur lors de la reprise de l\'abonnement',
              type: 'is-danger',
            });
          } finally {
            loader.close();
          }
        },
      });
    },
  },
};
</script>

<template>
  <div>
    <!-- Statistics Cards -->
    <div class="columns mb-5">
      <div class="column">
        <div class="card">
          <div class="card-content has-text-centered">
            <p class="title is-4">{{ activeSubscribersCount }}</p>
            <p class="subtitle is-6">Abonnements actifs</p>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="card">
          <div class="card-content has-text-centered">
            <p class="title is-4">{{ totalRevenue }}</p>
            <p class="subtitle is-6">Revenus mensuels</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="columns">
      <div class="column">
        <b-field label="Filtrer par">
          <b-input placeholder="Filtrez par email, prénom, nom, tag" :value="searchTerm" expanded @input="onSearch" />
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Statut">
          <b-select v-model="statusFilter">
            <option value="active">Actifs</option>
            <option value="all">Tous</option>
            <option value="paused">En pause</option>
            <option value="cancelled">Annulés</option>
            <option value="expired">Expirés</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Trier par">
          <b-select v-model="sortField" @input="fetchResults">
            <option value="email">Email</option>
            <option value="firstname">Prénom</option>
            <option value="lastname">Nom</option>
            <option value="created_at">Date d'abonnement</option>
            <option value="status">Statut</option>
          </b-select>
          <p class="control">
            <b-tooltip :label="sortOrder == 'asc' ? 'croissant' : 'décroissant'">
              <b-button :icon-left="sortOrder == 'asc' ? 'arrow-up' : 'arrow-down'" @click="toggleOrder" />
            </b-tooltip>
          </p>
        </b-field>
      </div>
    </div>

    <!-- Results summary -->
    <div class="mb-3 level is-flex-wrap-wrap">
      <div class="level-left">
        <p class="py-3 mr-3">
          Résultat :
          <strong>
            {{ filteredSubscribers.length }} abonnement{{ filteredSubscribers.length > 1 ? 's' : '' }}
          </strong>
        </p>
      </div>
      <div class="level-right">
        <b-dropdown position="is-bottom-left" aria-role="list">
          <template #trigger>
            <b-button type="is-text" icon-left="ellipsis-h" />
          </template>

          <b-dropdown-item aria-role="listitem" @click="cancelAllMemberships">
            <b-icon icon="cancel" size="is-small" />
            Annuler tous les abonnements
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Subscribers Table -->
    <div class="box is-relative">
      <b-table class="is-middle" :data="filteredSubscribers" :loading="loading" detailed backend-sorting paginated
        backend-pagination :total="subscribers.meta.total" :per-page="subscribers.meta.per_page" @sort="onSort"
        @page-change="onPageChange">

        <b-table-column v-slot="{ row }" field="firstname" label="Abonné" sortable>
          <router-link
            class="subscriber-link is-block mb-3"
            :to="{name: 'customer', params: {uuid: row.customer.uuid}}"
          >
            <div>
              {{ row.customer.firstname }} {{ row.customer.lastname }}
              <br>
              <strong>{{ row.customer.email }}</strong>
            </div>
          </router-link>
          <b-tooltip v-if="row.customer.crm_tags && row.customer.crm_tags.length" class="mt-2" :active="!hasExpertPlan"
            multilined>
            <template #content>
              Vous devez souscrire à un forfait EXPERT pour voir les tags
            </template>
            <p class="tags" :style="hasExpertPlan ? null : { filter: 'blur(4px)', userSelect: 'none' }">
              <span class="tag is-outlined" v-for="tag in row.customer.crm_tags" :key="tag">
                {{ tag }}
              </span>
            </p>
          </b-tooltip>
        </b-table-column>

        <b-table-column v-slot="{ row }" field="status" label="Statut" sortable>
          <b-tag :type="getStatusTag(row.status).type">
            {{ getStatusTag(row.status).text }}
          </b-tag>
        </b-table-column>

        <b-table-column v-slot="{ row }" label="Dates" numeric>
          <div class="is-size-7">
            <p>
              <b-tooltip :label="formatDate(row.created_at)">
                <strong>Débuté</strong>
                {{ formatDateFromNow(row.created_at) }}
              </b-tooltip>
            </p>
            <p v-if="row.next_billing_date" class="mt-1">
              <b-tooltip :label="formatDate(row.next_billing_date)">
                <strong>Prochaine facturation</strong>
                {{ formatDateFromNow(row.next_billing_date) }}
              </b-tooltip>
            </p>
            <p v-if="row.cancelled_at" class="mt-1 has-text-danger">
              <b-tooltip :label="formatDate(row.cancelled_at)">
                <strong>Annulé</strong>
                {{ formatDateFromNow(row.cancelled_at) }}
              </b-tooltip>
            </p>
          </div>
        </b-table-column>

        <b-table-column v-slot="{ row }" label="Tarif" numeric>
          <div>
            <div v-if="hasDiscount(row)">
              <span class="has-text-grey-dark" style="text-decoration: line-through;">{{ formatPrice(row.price.amount)
                }}</span>
              <strong class="ml-2">{{ formatPrice(getEffectivePrice(row)) }}</strong>
            </div>
            <div v-else>
              <strong>{{ formatPrice(getEffectivePrice(row)) }}</strong>
            </div>
            <br>
            <em class="is-size-7">
              {{ getSubscriptionFrequency(row) }}
            </em>
          </div>
        </b-table-column>

        <b-table-column v-slot="{ row }" width="40">
          <b-dropdown position="is-bottom-left" aria-role="list">
            <template #trigger>
              <b-button type="is-text" icon-left="ellipsis-h" />
            </template>

            <b-dropdown-item
              v-if="row.status === 'active' || row.status === 'cancel_at_period_end'"
              aria-role="listitem"
              @click="pauseMembership(row)"
            >
              <b-icon icon="pause" size="is-small" />
              Mettre en pause
            </b-dropdown-item>

            <b-dropdown-item
              v-if="row.status === 'paused'"
              aria-role="listitem"
              @click="resumeMembership(row)"
            >
              <b-icon icon="play" size="is-small" />
              Reprendre
            </b-dropdown-item>

            <b-dropdown-item
              v-if="row.status === 'active'"
              aria-role="listitem"
              @click="cancelMembership(row)"
            >
              <b-icon icon="cancel" size="is-small" />
              Annuler l'abonnement
            </b-dropdown-item>

            <b-dropdown-item v-if="row.status === 'cancelled'" aria-role="listitem">
              <b-icon icon="refresh" size="is-small" />
              Réactiver l'abonnement
            </b-dropdown-item>
          </b-dropdown>
        </b-table-column>

        <template #detail="{ row }">
          <div class="content">
            <p><strong>ID Stripe:</strong> {{ row.stripe_subscription_id || 'N/A' }}</p>
            <p><strong>Fréquence:</strong> {{ getSubscriptionFrequency(row) }}</p>
            <p>
              <strong>Prix:</strong>
              <span v-if="hasDiscount(row)">
                <span class="has-text-grey-dark" style="text-decoration: line-through;">{{ formatPrice(row.price.amount)
                  }}</span>
                <strong class="ml-2">{{ formatPrice(getEffectivePrice(row)) }}</strong>
              </span>
              <span v-else>
                {{ formatPrice(getEffectivePrice(row)) }}
              </span>
            </p>
            <div v-if="row.payment_history && row.payment_history.length">
              <p><strong>Historique des paiements:</strong></p>
              <div class="table-container">
                <table class="table is-narrow is-fullwidth">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Montant</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in row.payment_history" :key="payment.id">
                      <td>{{ formatDate(payment.created_at) }}</td>
                      <td>{{ formatPrice(payment.amount) }}</td>
                      <td>
                        <b-tag :type="payment.status === 'succeeded' ? 'is-success' : 'is-danger'">
                          {{ payment.status === 'succeeded' ? 'Réussi' : 'Échoué' }}
                        </b-tag>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <template #empty>
          <div class="p-5 has-text-centered">
            <p>Aucun abonnement pour le moment</p>
            <p class="is-size-7 mt-2">Les abonnements apparaîtront ici une fois qu'ils se seront inscrits à votre plan.
            </p>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .card-content {
    padding: 1.5rem;
  }
}

.table-container {
  max-height: 300px;
  overflow-y: auto;
}

.subscriber-link {
  color: $black;

  &:hover {
    color: $theme_color_primary_hover;
  }
}
</style>
